import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { ApiError } from "../../api/errors";
import {
  getOperatorSlug, normaliseSlug, PORTAL_BASE, saveOperatorSlug, slugProblem, type OperatorSlug,
} from "../../api/operatorSlug";

const refusal = (e: unknown) =>
  e instanceof ApiError && e.kind === "forbidden"
    ? "Refused: your role may not change the short name for this namespace, or it is outside your subtree."
    : e instanceof Error
      ? e.message
      : String(e);

const when = (iso: string) =>
  new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "medium", timeStyle: "short" }) + " IST";

/**
 * The operator's short name: the address of its customer portal,
 * ankhos.io/o/<short name>, where subscribers sign in with their mobile to see
 * their account and pay.
 *
 * 🔑 Same grant as the portal contact card: an Operator may set its own.
 * radaccess allows one change every 30 days; an old name keeps redirecting.
 */
export function PortalAddressCard({ ns }: { ns: string }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const load = useQuery({
    queryKey: ["operatorSlug", ns],
    enabled: Boolean(identity.token && ns),
    retry: false,
    queryFn: async () => (await getOperatorSlug(identity, ns)).data,
  });
  const [draft, setDraft] = useState("");
  useEffect(() => {
    if (load.data) setDraft(load.data.slug);
  }, [load.data]);

  const save = useMutation({
    mutationFn: async () => (await saveOperatorSlug(identity, ns, draft)).data,
    onSuccess: (s: OperatorSlug) => {
      qc.setQueryData(["operatorSlug", ns], s);
    },
  });

  const current = load.data;
  const slug = normaliseSlug(draft);
  const problem = slugProblem(slug);
  const unchanged = Boolean(current && slug === current.slug);
  const locked = Boolean(current?.nextChangeAt);

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Your customer portal address</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Your subscribers open this address and sign in with their mobile number to see their plan, receipts and pay. Choose a
        short name for <b>{ns}</b>. You can change it once every 30 days; the old address keeps working and sends people to
        the new one.
      </Typography>

      {load.isError && (
        <Alert severity={load.error instanceof ApiError && load.error.kind === "forbidden" ? "warning" : "error"} sx={{ mb: 2 }}>
          <AlertTitle>Could not load the short name</AlertTitle>
          {refusal(load.error)}
        </Alert>
      )}

      {current?.slug && (
        <Typography variant="body2" sx={{ mb: 2 }}>
          Address now: <b>{PORTAL_BASE}{current.slug}</b>
          {current.changedAt && <> · set {when(current.changedAt)}</>}
        </Typography>
      )}

      <TextField
        fullWidth size="small" label="Short name" placeholder="lotusnet"
        value={draft}
        disabled={load.isLoading || load.isError}
        onChange={(e) => { save.reset(); setDraft(e.target.value); }}
        error={Boolean(draft) && Boolean(problem)}
        helperText={
          draft && problem
            ? problem
            : slug
              ? `${PORTAL_BASE}${slug}`
              : "3 to 30 characters: lower case letters, digits and single hyphens."
        }
      />

      {locked && current?.nextChangeAt && (
        <Alert severity="info" sx={{ mt: 1 }}>
          Changed recently. The next change is allowed from {when(current.nextChangeAt)}.
        </Alert>
      )}
      {current && current.retired.length > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Old addresses still redirecting here: {current.retired.map((r) => PORTAL_BASE + r).join(", ")}
        </Typography>
      )}

      {save.error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          <AlertTitle>Not saved</AlertTitle>
          {refusal(save.error)}
        </Alert>
      )}
      {save.isSuccess && <Alert severity="success" sx={{ mt: 1 }}>Saved.</Alert>}

      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        <Button
          variant="contained"
          disabled={!current || Boolean(problem) || unchanged || locked || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? "Saving…" : current?.slug ? "Change short name" : "Save short name"}
        </Button>
      </Stack>
    </Paper>
  );
}
